<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <!-- Navbar -->
    <nav class="bg-slate-900/85 backdrop-blur-md border-b border-cyan-400/20 fixed top-0 left-0 right-0 z-50 shadow-[0_0_20px_rgba(34,211,238,0.12),0_12px_36px_-8px_rgba(34,211,238,0.14)]">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-20">
          <button
            type="button"
            @click="goToHome"
            class="flex shrink-0 items-center self-center hover:opacity-90 transition-opacity cursor-pointer min-w-0"
          >
            <img
              :src="logoUrl"
              alt="PowerAgent"
              class="block h-auto w-auto max-h-14 max-w-[min(100%,15rem)] object-contain object-left sm:max-h-16 md:max-h-20 md:max-w-none"
            />
          </button>

          <div class="flex items-center gap-2 sm:gap-6">
            <button
              @click="goToShowcase"
              class="inline-flex text-xs sm:text-sm font-medium text-slate-300 hover:text-cyan-300 hover:bg-cyan-400/10 px-2 sm:px-3 py-2 rounded-lg transition whitespace-nowrap"
            >
              Início
            </button>

            <button
              @click="goToAbout"
              class="inline-flex text-xs sm:text-sm font-medium text-slate-300 hover:text-cyan-300 hover:bg-cyan-400/10 px-2 sm:px-3 py-2 rounded-lg transition whitespace-nowrap"
            >
              Quem Somos
            </button>

            <!-- Acesso ao ambiente (login tenant) -->
            <div class="relative">
              <button
                @click="toggleHostMenu"
                class="flex items-center gap-1 sm:gap-2 text-cyan-300 font-semibold px-2 sm:px-4 py-2 rounded-lg hover:bg-cyan-400/10 hover:text-cyan-200 transition whitespace-nowrap text-xs sm:text-base"
              >
                <span class="hidden sm:inline">Acesso ao ambiente</span>
                <span class="sm:hidden">Ambiente</span>
                <svg
                  class="w-4 h-4 transition-transform"
                  :class="{ 'rotate-180': isHostMenuOpen }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <div
                  v-if="isHostMenuOpen"
                  class="absolute right-0 mt-2 w-52 rounded-xl bg-[#435b7c]/95 backdrop-blur-sm border border-cyan-400/25 shadow-2xl shadow-black/45 py-2 z-50"
                >
                  <p class="text-center px-4 pb-2 text-[12px] text-amber-300/90 border-b border-cyan-200/15">
                    Área exclusiva para equipes do ambiente
                  </p>
                  <button
                    @click="openHostLogin"
                    class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-cyan-400/10 hover:text-cyan-200 transition"
                  >
                    Entrar
                  </button>
                  <button
                    @click="openHostRegister"
                    class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-cyan-400/10 hover:text-cyan-200 transition"
                  >
                    Criar Conta
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <section v-if="landingSection === 'showcase'" class="pt-28 pb-16 px-4">
      <div class="max-w-5xl mx-auto rounded-xl border border-cyan-400/30 bg-slate-900/80 backdrop-blur-sm shadow-xl shadow-black/30 p-8 md:p-10">
        <h1 class="text-4xl font-bold text-slate-100 mb-4">PowerAgents</h1>
        <p class="text-lg text-slate-300 mb-6 leading-relaxed max-w-3xl">
          Orquestre <span class="font-semibold text-cyan-300">agentes de IA</span> que falam com os seus sistemas de
          verdade —
          <span class="font-semibold text-cyan-300">APIs</span>, <span class="font-semibold text-cyan-300">bancos</span>,
          <span class="font-semibold text-cyan-300">e-mail</span> e <span class="font-semibold text-cyan-300">WhatsApp</span>
          — e acompanhe cada <span class="font-semibold text-cyan-300">execução</span> em um só lugar.
        </p>
        <div class="grid sm:grid-cols-3 gap-4 text-sm text-slate-300">
          <div class="rounded-lg border border-cyan-400/20 bg-slate-800/50 p-4">
            <p class="font-semibold mb-1 text-slate-100">Rede inteligente de agentes</p>
            <p>
              <span class="font-semibold text-cyan-300">Coordenadores</span> e
              <span class="font-semibold text-cyan-300">executores</span> trabalhando em conjunto: você define quem chama
              quem, as especialidades (dados, conhecimento, cadastros, e-mail) e visualiza o
              <span class="font-semibold text-cyan-300">mapa das ligações</span> no seu ambiente.
            </p>
          </div>
          <div class="rounded-lg border border-cyan-400/20 bg-slate-800/50 p-4">
            <p class="font-semibold mb-1 text-slate-100">Conectores reais, respostas acionáveis</p>
            <p>
              Integre <span class="font-semibold text-cyan-300">APIs</span>,
              <span class="font-semibold text-cyan-300">visões de banco de dados</span>, caixas de entrada e envio de
              mensagens. Os <span class="font-semibold text-cyan-300">agentes</span> operam em cima do que a sua operação
              já usa no dia a dia.
            </p>
          </div>
          <div class="rounded-lg border border-cyan-400/20 bg-slate-800/50 p-4">
            <p class="font-semibold mb-1 text-slate-100">Chat com contexto e painel de execuções</p>
            <p>
              Converse com <span class="font-semibold text-cyan-300">coordenadores</span> autorizados, mantenha histórico
              e acompanhe filas, status e detalhes das
              <span class="font-semibold text-cyan-300">execuções</span> — da fila ao resultado.
            </p>
          </div>
        </div>
        <p class="mt-6 text-xs text-slate-500 leading-relaxed max-w-3xl">
          <span class="font-semibold text-cyan-400/90">Ambientes isolados</span> por cliente,
          <span class="font-semibold text-cyan-400/90">acesso controlado</span> e
          <span class="font-semibold text-cyan-400/90">homologação</span> de novos ambientes quando a sua operação
          exigir.
        </p>
      </div>
    </section>
    <About v-else />

    <!-- Login Modal -->
    <div v-if="showLoginModal" class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 flex items-center justify-center p-4">
      <div class="relative">
        <button @click="activeModal = null" class="absolute -top-10 right-0 text-slate-200 font-bold text-lg hover:text-cyan-300 transition">
          ✕ Fechar
        </button>
        <TenantLogin
          key="login"
          @login-success="handleLoginSuccess"
          @switch-to-register="switchToRegister"
        />
      </div>
    </div>

    <!-- Register Modal -->
    <div v-if="showRegisterModal" class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 flex items-center justify-center p-4">
      <div class="relative w-full max-w-6xl">
        <button @click="activeModal = null" class="absolute -top-10 right-0 text-slate-200 font-bold text-lg hover:text-cyan-300 transition z-10">
          ✕ Fechar
        </button>
        <TenantRegister
          key="register"
          @register-success="handleRegisterSuccess"
          @switch-to-login="switchToLogin"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TenantLogin from './TenantLogin.vue'
import TenantRegister from './TenantRegister.vue'
import About from './About.vue'
import { navigateTo } from '../router'

export default {
  components: { TenantLogin, TenantRegister, About },
  emits: ['auth-success'],

  props: {
    landingSection: {
      type: String,
      default: 'showcase',
      validator: (v) => ['showcase', 'about'].includes(v)
    }
  },

  data() {
    return {
      logoUrl: '/poweragent-logo.png',
      activeModal: null, // 'login', 'register', ou null
      isHostMenuOpen: false
    }
  },

  computed: {
    showLoginModal() {
      return this.activeModal === 'login'
    },
    showRegisterModal() {
      return this.activeModal === 'register'
    }
  },

  methods: {
    handleLoginSuccess(data) {
      const masterCode = data.tenant.master_code
      window.history.replaceState({}, document.title, `/${masterCode}`)
      this.$emit('auth-success', data)
    },

    handleRegisterSuccess(data) {
      const masterCode = data.tenant.master_code
      window.history.replaceState({}, document.title, `/${masterCode}`)
      // Tenant inativo: não faz login; recarrega na URL do master_code (estado “pendente de homologação”)
      if (data.tenant.active === false || (!data.tenantToken && !data.userToken)) {
        this.closeAuthModal()
        window.location.reload()
        return
      }
      this.$emit('auth-success', data)
    },

    switchToRegister() {
      this.activeModal = 'register'
      this.isHostMenuOpen = false
    },

    switchToLogin() {
      this.activeModal = 'login'
      this.isHostMenuOpen = false
    },

    goToHome() {
      this.activeModal = null
      this.isHostMenuOpen = false
      navigateTo('dashboard')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    closeAuthModal() {
      this.activeModal = null
      this.isHostMenuOpen = false
    },

    toggleHostMenu() {
      this.isHostMenuOpen = !this.isHostMenuOpen
    },

    openHostLogin() {
      this.activeModal = 'login'
      this.isHostMenuOpen = false
    },

    openHostRegister() {
      this.activeModal = 'register'
      this.isHostMenuOpen = false
    },

    goToShowcase() {
      this.activeModal = null
      this.isHostMenuOpen = false
      navigateTo('dashboard')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    goToAbout() {
      this.activeModal = null
      this.isHostMenuOpen = false
      navigateTo('about')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>
